import {
  Injectable,
  inject,
  DestroyRef,
  signal,
  computed,
} from '@angular/core';
import { TokenService } from './token.service';
import { Router } from '@angular/router';
import { ProfileModel } from '../models';
import { ApiService } from '@core/services';
import { Cookies } from 'typescript-cookie';
import { Observable, of, tap } from 'rxjs';

import { BussinesService } from './bussines.service';
import { ThemeService } from '../../admin';
import { LogoService } from '@core/services';
import { AuthStatus, ValidationModel } from '../models';
import { ValidateLogin } from '../../admin';
import { Session } from '@core/models';
import { AccountStatusService } from './account-status.service';
import {
  Auth,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
} from '@angular/fire/auth';
import { PublicClientApplication, type AuthenticationResult } from '@azure/msal-browser';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _authStatus = signal<AuthStatus>('checking');
  private _currentUser = signal<ValidateLogin | Session | null>(null);
  
  // Configuración de MSAL para Microsoft
  private msalInstance: PublicClientApplication;
  private readonly msalConfig = {
    auth: {
      clientId: environment.microsoftAuth.clientId,
      authority: `https://login.microsoftonline.com/${environment.microsoftAuth.tenantId}`,
      redirectUri: window.location.origin,
    },
    cache: {
      cacheLocation: 'sessionStorage' as const,
      storeAuthStateInCookie: false,
    },
  };

  public authStatus = computed(() => this._authStatus());
  public currentUser = computed(() => this._currentUser());

  private router = inject(Router);
  private tokenService = inject(TokenService);
  private themeService = inject(ThemeService);
  private logoService = inject(LogoService);
  private apiService = inject(ApiService);
  private bussinesService = inject(BussinesService);
  private destroyRef = inject(DestroyRef);
  #accountStatusService = inject(AccountStatusService);
  #auth = inject(Auth);

  constructor() {
    // Inicializar MSAL
    this.msalInstance = new PublicClientApplication(this.msalConfig);
    
    // Inicializar y manejar redirect de Microsoft
    this.msalInstance.initialize().then(() => {
      this.handleMicrosoftRedirect().then(() => {
        this.checkAuthStatus().subscribe();
      });
    });
  }

  checkAuthStatus(): Observable<boolean> {
    const token = this.tokenService.getToken();

    if (!token) {
      // No hacer logout si estamos procesando un redirect de OAuth
      const currentUrl = window.location.href;
      if (!currentUrl.includes('code=') && !currentUrl.includes('state=')) {
        this.logout();
      }
      return of(false);
    }

    const expirationDate = new Date(this.tokenService.getTokenExpiration()!);
    const currentTimestamp = new Date().getTime();

    if (currentTimestamp < expirationDate.getTime()) {
      return of(this.setAuthentication());
    } else {
      this._currentUser.set(null);
      this._authStatus.set('noAuthenticated');
      return of(false);
    }
  }

  sendLogin(data: ProfileModel) {
    return this.apiService.store('auth/login', data);
  }

  sendResetPassword(email: string | { [property: string]: string }) {
    return this.apiService.post(`auth/resetPassword?email=${email}`, {});
  }

  confirmResetPassword(data: { codigo: string; password: string; confirmPassword: string }) {
    return this.apiService.post(`auth/changePassword`, data);
  }

  validationLogin(data: ValidationModel) {
    return this.apiService
      .post<Session>(
        `auth/validateLogin?token=${data.token}&worker=${data.idWorker}&application=false`,
        {}
      )
      .pipe(
        tap(({ reply }) => {
          console.log('dadtos en filtro', reply.role, '= = = ', reply.business);
          console.log('reply.business', reply.business);
          if (reply.role !== 'SYSADMIN' && reply.business) {
            this.tokenService.saveTheme(reply.business.themeId);
            this.tokenService.saveCompany(reply.business.name);
            this.tokenService.saveLogo(reply.business.logoId);
          }
          this._currentUser.set(reply);
          Cookies.set('username', reply.userName);
          Cookies.set('role', reply.role);
          Cookies.set('workerId', reply.workerId);
          Cookies.set('email', reply.email);
          this.setAuthentication();
        })
      );
  }

  async loginWithGoogle() {
    const resp = await signInWithPopup(this.#auth, new GoogleAuthProvider());
    const response: any = resp;

    console.log('resp', response);
    return await this.apiService
      .post<Session>('auth/login/googleAdmin', {
        platform: 'firebase',
        token: response.user.accessToken,
      })
      .pipe(
        tap(({ reply }) => {
          console.log('dadtos en filtro', reply.role, '= = = ', reply.business);
          console.log('reply.business', reply.business);
          if (reply.role !== 'SYSADMIN' && reply.business) {
            this.tokenService.saveTheme(reply.business.themeId);
            this.tokenService.saveCompany(reply.business.name);
            this.tokenService.saveLogo(reply.business.logoId);
          }
          this._currentUser.set(reply);
          Cookies.set('username', reply.userName);
          Cookies.set('role', reply.role);
          Cookies.set('workerId', reply.workerId);
          Cookies.set('email', reply.email);
          this.setAuthentication();
          this.setAuthentication();
        })
      );
  }

  async loginWithMicrosoft() {
    console.log('🚀 Iniciando login con Microsoft usando MSAL...');
    
    const loginRequest = {
      scopes: ['openid', 'profile', 'email', 'User.Read'],
      prompt: 'select_account' as const,
    };

    try {
      await this.msalInstance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('❌ Error al iniciar login con Microsoft:', error);
      throw error;
    }
  }

  // Manejar el resultado del redirect de Microsoft con MSAL
  private async handleMicrosoftRedirect() {
    console.log('🔍 Verificando redirect de Microsoft con MSAL...');
    try {
      const result: AuthenticationResult | null = await this.msalInstance.handleRedirectPromise();
      console.log('📥 Resultado de MSAL:', result);
      
      if (result && result.account) {
        console.log('✅ Usuario autenticado con MSAL:', result.account);
        console.log('🔑 ID Token:', result.idToken);
        
        this.apiService
          .post<Session>('auth/login/office365Admin', {
            email: result.account.username,
            token: result.idToken,
          })
          .subscribe(({ reply }) => {
            console.log('🎉 Login exitoso en backend:', reply);
            
            // Guardar el token JWT
            if (reply.token) {
              this.tokenService.saveToken(reply.token);
            }
            
            // Guardar theme, logo y company si no es SYSADMIN
            if (reply.role !== 'SYSADMIN' && reply.business) {
              this.tokenService.saveTheme(reply.business.themeId);
              this.tokenService.saveCompany(reply.business.name);
              this.tokenService.saveLogo(reply.business.logoId);
              this.bussinesService.saveBussinesId(reply.business.businessId);
            }
            
            this._currentUser.set(reply);
            Cookies.set('username', reply.userName);
            Cookies.set('role', reply.role);
            Cookies.set('workerId', reply.workerId);
            Cookies.set('email', reply.email);
            this.setAuthentication();
            
            // Redirigir al dashboard después del login
            this.router.navigateByUrl('/admin/dashboard');
          });
      } else {
        console.log('ℹ️ No hay redirect pendiente de Microsoft');
      }
    } catch (error) {
      console.error('❌ Error en autenticación de Microsoft:', error);
    }
  }

  async logout() {
    await this.logoutFirebase();
    this.tokenService.removeToken();
    this.bussinesService.removeBussinesId();
    this.tokenService.removeTheme();
    this.tokenService.removeCompany();
    this.tokenService.removeLogo();
    this.#accountStatusService.deleteAccountStatus();
    Cookies.remove('user');
    Cookies.remove('username');
    Cookies.remove('workerId');
    Cookies.remove('role');
    Cookies.remove('email');
    this._currentUser.set(null);
    this.router.navigateByUrl('/auth');
    localStorage.removeItem('permissions');
    // this.themeService.setTheme(null);
    // this.logoService.setLogo(null);
  }

  logoutFirebase() {
    return signOut(this.#auth);
  }

  private setAuthentication() {
    this._authStatus.set('authenticated');

    return true;
  }
}
